import { useAuth0, type RedirectLoginOptions } from '@auth0/auth0-react';
import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { driverApi } from '../services/driverApi';
import { isAuth0SessionRecoverableError } from '../utils/auth0Recovery';
import { isEmbeddedMode } from '../utils/embeddedMode';
import {
  isNative,
  refreshNativeToken,
  signOutNative,
} from '../lib/nativeBridge';
import { nativeSession } from '../utils/nativeSession';
import { getDriverTokenManager } from '../utils/tokenManager';
import { devLog, devWarn } from '../utils/devLog';

interface HybridAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  error: any;
  getAccessTokenSilently: () => Promise<string>;
  /** Auth0's, on the web; ignored inside the native app, which signs in itself. */
  loginWithRedirect: (options?: RedirectLoginOptions) => void | Promise<void>;
  logout: () => void;
}

// ─── Embedded mode: one session for every hook instance ─────────────────────
// The token lives in nativeSession, not in component state, so a screen that
// mounts later sees the same session Home is already using.

function activateNativeAuth(token: string) {
  driverApi.setAuthToken(token);

  // Every refresh (a token near expiry, a 401's retry) goes through this
  // fetcher. A v2 wrapper is asked for a fresh token; a v1 one, or a v2 one
  // that does not answer, leaves the stored one, which the wrapper rewrites
  // when the app comes back to the foreground.
  getDriverTokenManager().initialize(async () => {
    if (isNative()) {
      const fresh = await refreshNativeToken();
      if (fresh) {
        localStorage.setItem('access_token', fresh);
        driverApi.setAuthToken(fresh);
        return fresh;
      }
    }
    const stored = localStorage.getItem('access_token');
    if (!stored) throw new Error('No access_token in localStorage');
    return stored;
  });
  getDriverTokenManager().updateCachedToken(token);

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    (window as any).__nativeAuthUser = {
      sub: payload.sub,
      email: payload.email,
      name: payload.name || payload.email,
      picture: payload.picture,
      email_verified: payload.email_verified,
      updated_at: payload.updated_at,
    };
  } catch (err) {
    console.error('[HybridAuth] Error decoding token:', err);
  }

  devLog('[HybridAuth] Native authentication active');
  nativeSession.activate(token);
}

function clearNativeAuth() {
  localStorage.removeItem('access_token');
  driverApi.setAuthToken('');
  nativeSession.clear();
}

export function useHybridAuth(): HybridAuthState {
  const auth0 = useAuth0();
  const nativeToken = useSyncExternalStore(
    nativeSession.subscribe,
    nativeSession.getToken,
    nativeSession.getToken
  );
  const isNativeAuth = nativeToken !== null;
  const waitingForNative = useSyncExternalStore(
    nativeSession.subscribe,
    nativeSession.isWaiting,
    nativeSession.isWaiting
  );
  const sessionRecoveryInProgressRef = useRef(false);

  useEffect(() => {
    if (auth0.isAuthenticated) {
      sessionRecoveryInProgressRef.current = false;
    }
  }, [auth0.isAuthenticated]);

  const fetchAuth0AccessToken = useCallback(
    async (bypassCache?: boolean): Promise<string> => {
      try {
        return await auth0.getAccessTokenSilently({
          cacheMode: bypassCache ? 'off' : 'on',
        });
      } catch (err) {
        if (
          isAuth0SessionRecoverableError(err) &&
          !sessionRecoveryInProgressRef.current
        ) {
          sessionRecoveryInProgressRef.current = true;
          devWarn(
            '[HybridAuth] Recoverable auth error, logging out to refresh session'
          );
          auth0.logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          });
        }
        throw err;
      }
    },
    [auth0.getAccessTokenSilently, auth0.logout]
  );

  // ─── Web mode: wire Auth0 into TokenManager once Auth0 is ready ───────────
  useEffect(() => {
    if (isEmbeddedMode()) return;
    if (auth0.isLoading) return;
    if (!auth0.isAuthenticated) return;

    getDriverTokenManager().initialize(async (bypassCache?: boolean) => {
      return fetchAuth0AccessToken(bypassCache);
    });

    devLog('[HybridAuth] TokenManager wired to Auth0');
  }, [auth0.isLoading, auth0.isAuthenticated, fetchAuth0AccessToken]);

  // ─── Web mode: refresh on tab visibility ─────────────────────────────────
  useEffect(() => {
    if (isEmbeddedMode()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        getDriverTokenManager().handleTabVisible();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // ─── Embedded mode: adopt the shell's token once per page load ───────────
  useEffect(() => {
    nativeSession.bootstrap(activateNativeAuth);
  }, []);

  // ─── getAccessTokenSilently ───────────────────────────────────────────────

  const getAccessTokenSilentlyNative =
    useCallback(async (): Promise<string> => {
      const token = await getDriverTokenManager().getToken();
      if (token) return token;
      // The cached token aged out and the storage key is gone, usually
      // because a 401 handler removed it. No request will go out, so nothing
      // else can say the session is dead. Carry the status so the UI routes
      // to sign in.
      throw Object.assign(new Error('No native token available'), {
        status: 401,
      });
    }, []);

  const getAccessTokenSilentlyAuth0 = useCallback(async (): Promise<string> => {
    const token = await getDriverTokenManager().getToken();
    if (token) return token;
    // TokenManager not yet initialised — fall back to Auth0 directly
    return fetchAuth0AccessToken();
  }, [fetchAuth0AccessToken]);

  // ─── Return values ────────────────────────────────────────────────────────

  const nativeAuthState = useMemo(
    () => ({
      isAuthenticated: true,
      isLoading: false,
      user: (window as any).__nativeAuthUser || null,
      error: null,
      getAccessTokenSilently: getAccessTokenSilentlyNative,
      loginWithRedirect: () => {
        devWarn(
          '[HybridAuth] loginWithRedirect called in native mode — ignored'
        );
      },
      logout: () => {
        devLog('[HybridAuth] logout called (native)');
        clearNativeAuth();
        // A v2 wrapper takes the bridge's message; an older one its global.
        if (signOutNative()) return;
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'signOut' })
          );
        }
      },
    }),
    [getAccessTokenSilentlyNative]
  );

  const auth0State = useMemo(
    () => ({
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading,
      user: auth0.user,
      error: auth0.error,
      getAccessTokenSilently: getAccessTokenSilentlyAuth0,
      loginWithRedirect: auth0.loginWithRedirect,
      // Back to this app's sign-in. With no returnTo, Auth0 sends a
      // signed-out driver to the tenant's default, the dashboard or a
      // /landing page that does not exist.
      logout: () => {
        auth0.logout({ logoutParams: { returnTo: window.location.origin } });
      },
    }),
    [
      auth0.isAuthenticated,
      auth0.isLoading,
      auth0.user,
      auth0.error,
      getAccessTokenSilentlyAuth0,
      auth0.loginWithRedirect,
      auth0.logout,
    ]
  );

  // Signed out in a v2 wrapper: the landing page, whose buttons ask the
  // wrapper to sign in (useSignIn). Auth0's own state is a browser's and
  // says nothing about the wrapper's session.
  const nativeSignedOutState = useMemo(
    () => ({
      isAuthenticated: false,
      isLoading: waitingForNative,
      user: null,
      error: null,
      getAccessTokenSilently: getAccessTokenSilentlyNative,
      loginWithRedirect: () => {
        devWarn(
          '[HybridAuth] loginWithRedirect called in native mode — ignored'
        );
      },
      logout: () => {},
    }),
    [waitingForNative, getAccessTokenSilentlyNative]
  );

  if (isNativeAuth) {
    return nativeAuthState;
  }

  if (isNative()) {
    return nativeSignedOutState;
  }

  return auth0State;
}
